import { useState, useEffect } from 'react';
import {
  Activity,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  LogOut,
  Clock,
  User,
  Trash2
} from 'lucide-react';
import api from '../../services/api';

const roleBadges = {
  admin: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300',
  technician: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300',
  company_admin: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  user: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300',
};

export default function SessionsPage() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0,
  });

  // Confirmation modal state
  const [revokeSession, setRevokeSession] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const fetchSessions = async (page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get(`/admin/sessions?page=${page}&limit=${pagination.limit}`);
      setSessions(response.data.sessions);
      setPagination(response.data.pagination);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const handleRevoke = async () => {
    if (!revokeSession) return;

    setRevoking(true);
    try {
      await api.delete(`/admin/sessions/${revokeSession.sid}`);
      setSessions(sessions.filter(s => s.sid !== revokeSession.sid));
      setRevokeSession(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke session');
    } finally {
      setRevoking(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTimeRemaining = (expireDate) => {
    const now = new Date();
    const expire = new Date(expireDate);
    const diff = expire - now;

    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h`;
    }

    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">Active Sessions</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Manage active user sessions across the portal
          </p>
        </div>
        <button
          onClick={() => fetchSessions(pagination.page)}
          className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700"
        >
          Refresh
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3 text-red-700 dark:text-red-300">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Sessions Table */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 dark:bg-neutral-700/50 border-b border-neutral-200 dark:border-neutral-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Session ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Expires
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Time Left
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-neutral-500 dark:text-neutral-400">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                      Loading sessions...
                    </div>
                  </td>
                </tr>
              ) : sessions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-neutral-500 dark:text-neutral-400">
                    <Activity className="w-8 h-8 mx-auto mb-2 text-neutral-400 dark:text-neutral-500" />
                    No active sessions
                  </td>
                </tr>
              ) : (
                sessions.map((session) => (
                  <tr key={session.sid} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                    <td className="px-6 py-4">
                      {session.email ? (
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center">
                            <User className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          </div>
                          <div>
                            <p className="font-medium text-neutral-900 dark:text-white">
                              {session.first_name} {session.last_name}
                            </p>
                            <p className="text-sm text-neutral-500 dark:text-neutral-400">{session.email}</p>
                          </div>
                        </div>
                      ) : (
                        <span className="text-neutral-400 dark:text-neutral-500">Anonymous</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {session.role && (
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${roleBadges[session.role] || roleBadges.user}`}>
                          {session.role.replace('_', ' ')}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <code className="text-xs text-neutral-600 dark:text-neutral-300 bg-neutral-100 dark:bg-neutral-700 px-2 py-1 rounded">
                        {session.sid.substring(0, 16)}...
                      </code>
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-600 dark:text-neutral-300">
                      {formatDate(session.expire)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-neutral-400" />
                        <span className="text-sm text-neutral-600 dark:text-neutral-300">
                          {getTimeRemaining(session.expire)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => setRevokeSession(session)}
                        className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title="Revoke session"
                      >
                        <LogOut className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-700 flex items-center justify-between">
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} sessions
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchSessions(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-neutral-300"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-neutral-600 dark:text-neutral-400">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => fetchSessions(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="p-2 rounded-lg border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-neutral-300"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Revoke Confirmation Modal */}
      {revokeSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setRevokeSession(null)} />
          <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                  Revoke Session
                </h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  This will log the user out immediately
                </p>
              </div>
            </div>

            <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-6">
              Are you sure you want to revoke the session for{' '}
              <strong>{revokeSession.email || 'this user'}</strong>?
              They will need to log in again to access the portal.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRevokeSession(null)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleRevoke}
                disabled={revoking}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {revoking ? 'Revoking...' : 'Revoke Session'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
